import Link from 'next/link';
import { useRouter } from 'next/router';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import classNames from 'classnames';
import style from './style.module.scss';

const mapStates = (state: any) => ({
  siteName: state.ui.siteName,
  user: { ...state.user.current },
  menus: state.ui.menus
});

const connector = connect(mapStates);

export type PropsFromRedux = ConnectedProps<typeof connector>;

type IProps = {
  forceShow?: boolean;
  fixed?: boolean;
  compact?: boolean;
} & PropsFromRedux;

function Footer({
  forceShow = false,
  fixed = false,
  compact = false,
  user = null,
  menus = []
}: IProps) {
  const { footerContent = '' } = useSelector((state: any) => state.ui);
  const router = useRouter();

  // Dynamic menus from the backend, scoped to the "footer" section
  const footerMenus = menus
    ?.filter((m) => m.section === 'footer')
    .map((m) => ({ ...m, _id: Math.random() }));

  const renderMenus = () => {
    if (!user?._id) {
      return (
        <ul>
          {footerMenus.map((item) => (
            <li
              key={item._id}
              className={router.pathname === item.path ? 'active' : ''}
            >
              <a
                href={item.path}
                rel="noreferrer"
                target={item.isNewTab ? '_blank' : undefined}
              >
                {item.title}
              </a>
            </li>
          ))}
        </ul>
      );
    }

    return (
      <ul>
        <li className={router.pathname === '/home' ? 'active' : ''}>
          <Link href="/home">Home</Link>
        </li>
        {footerMenus.map((item) => (
          <li
            key={item._id}
            className={router.pathname === item.path ? 'active' : ''}
          >
            <a
              href={item.path}
              rel="noreferrer"
              target={item.isNewTab ? '_blank' : undefined}
            >
              {item.title}
            </a>
          </li>
        ))}
      </ul>
    );
  };

  // Don't hide footer on home page anymore since it's now fixed at the bottom
  // On home page, footer is always shown (fixed), so this only applies to non-home pages
  if (!forceShow && !fixed && router.asPath.includes('/home')) return null;

  const isLoginPage = router.pathname === '/auth/login';

  // Policy pages (/page/*) should always use large footer, never compact
  const isPolicyPage = router.pathname.startsWith('/page/');
  const shouldUseCompact = compact && !isPolicyPage;

  return (
    <footer
      className={classNames(style['main-footer'], {
        [style['no-margin']]: isLoginPage,
        [style['fixed-footer']]: fixed,
        [style['compact-footer']]: shouldUseCompact
      })}
      id="main-footer"
    >
      <div className="main-container">
        <div className={style['footer-inner']}>

          {/* Dynamic nav links with pipe separators via CSS */}
          {renderMenus()}

          {/* Bottom block: company info + Trustpilot */}
          <div className={style['footer-bottom']}>

            {/* Server-rendered company / address HTML */}
            {footerContent && (
              <div
                className={style['footer-content']}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: footerContent }}
              />
            )}

          </div>
        </div>
      </div>
    </footer>
  );
}

export default connector(Footer);
